import { type CSSProperties, createContext } from 'react';

import Prism from 'prismjs';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-jsx';
import 'prismjs/components/prism-tsx';
import 'prismjs/components/prism-typescript';
import { Node } from 'slate';

export type CodeToken = { start: number; length: number; type: string };

// Line-number gutter sized to the block's longest number (read by codeBlock.css).
export const codeGutterStyle = (lineCount: number): CSSProperties =>
  ({ '--rte-gutter-digits': String(Math.max(1, lineCount)).length } as CSSProperties);

// Matches the browser's default `tab-size` of a <pre>.
const TAB_WIDTH = 8;

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

const indentColumns = (line: string): number | null => {
  if (!line.trim()) return null;
  let column = 0;
  for (const char of line) {
    if (char === ' ') column += 1;
    else if (char === '\t') column += TAB_WIDTH - (column % TAB_WIDTH);
    else break;
  }
  return column;
};

export type CodeIndentGuides = { unit: number; levels: number[] };

// VS Code-style indent guides: how many guide lines each line gets, and the
// indent step (in characters) between them. Blank lines keep the guides of the
// shallower neighbour so the lines don't break on empty rows.
export const codeIndentGuides = (lines: string[]): CodeIndentGuides => {
  const columns = lines.map(indentColumns);
  const unit = columns.reduce<number>((acc, column) => (column ? gcd(acc, column) : acc), 0);
  if (!unit) return { unit: 0, levels: lines.map(() => 0) };

  const raw = columns.map((column) => (column === null ? null : Math.floor(column / unit)));
  const levels = raw.map((level, index) => {
    if (level !== null) return level;
    const previous =
      raw
        .slice(0, index)
        .reverse()
        .find((value) => value !== null) ?? 0;
    const next = raw.slice(index + 1).find((value) => value !== null) ?? 0;
    return Math.min(previous, next);
  });

  return { unit, levels };
};

export const codeIndentStyle = (level = 0, unit = 0): CSSProperties =>
  ({ '--rte-indent-levels': level, '--rte-indent-unit': unit } as CSSProperties);

export const CodeIndentContext = createContext<CodeIndentGuides | null>(null);

export const codeBlockText = (block: { children: unknown[] }): string =>
  (block.children as Node[]).map((line) => Node.string(line)).join('\n');

export const DEFAULT_CODE_LANGUAGE = 'tsx';

export const detectCodeLanguage = (blockText: string): string => {
  const trimmed = blockText.trim();
  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // not strict JSON — fall through to TSX
    }
  }
  return DEFAULT_CODE_LANGUAGE;
};

const grammarFor = (language: string) =>
  Prism.languages[language] || Prism.languages.tsx || Prism.languages.javascript;

const flatten = (
  tokens: (string | Prism.Token)[],
  start: number,
  parentType: string | undefined,
  out: CodeToken[],
): number => {
  let offset = start;

  for (const token of tokens) {
    if (typeof token === 'string') {
      if (parentType && token.length)
        out.push({ start: offset, length: token.length, type: parentType });
      offset += token.length;
    } else {
      const type = token.type || parentType || '';
      if (typeof token.content === 'string') {
        if (token.content.length) out.push({ start: offset, length: token.content.length, type });
        offset += token.content.length;
      } else if (Array.isArray(token.content)) {
        offset = flatten(token.content as (string | Prism.Token)[], offset, type, out);
      } else {
        offset = flatten([token.content], offset, type, out);
      }
    }
  }

  return offset;
};

export const tokenizeCode = (line: string, language = DEFAULT_CODE_LANGUAGE): CodeToken[] => {
  if (!line) return [];
  try {
    const out: CodeToken[] = [];
    flatten(Prism.tokenize(line, grammarFor(language)), 0, undefined, out);
    return out;
  } catch {
    return [];
  }
};

export const CODE_TOKEN_CLASS: Record<string, string> = {
  comment: 'text-[#6A9955] italic',
  prolog: 'text-[#6A9955] italic',
  doctype: 'text-[#6A9955] italic',
  cdata: 'text-[#6A9955] italic',
  keyword: 'text-[#569CD6]',
  boolean: 'text-[#569CD6]',
  atrule: 'text-[#569CD6]',
  'attr-value': 'text-[#CE9178]',
  string: 'text-[#CE9178]',
  char: 'text-[#CE9178]',
  'template-string': 'text-[#CE9178]',
  regex: 'text-[#D16969]',
  number: 'text-[#B5CEA8]',
  constant: 'text-[#B5CEA8]',
  function: 'text-[#DCDCAA]',
  'function-variable': 'text-[#DCDCAA]',
  'class-name': 'text-[#4EC9B0]',
  'maybe-class-name': 'text-[#4EC9B0]',
  builtin: 'text-[#4EC9B0]',
  tag: 'text-[#569CD6]',
  'attr-name': 'text-[#9CDCFE]',
  property: 'text-[#9CDCFE]',
  'literal-property': 'text-[#9CDCFE]',
  parameter: 'text-[#9CDCFE]',
  operator: 'text-[#D4D4D4]',
  punctuation: 'text-[#808080]',
  deleted: 'text-[#D16969]',
  inserted: 'text-[#B5CEA8]',
};
